import React, { useEffect, useState } from "react";
import { TextInput, colors2, UI_BODY_BOLD, UI_CAPTION, useBannerContext } from "oolib";
import {
  ButtonGhost,
  ButtonPrimary,
} from "oolib/dist/v2/components/Buttons";
import { useCreatePendingDirectDataImport } from "../hooks/useCreatePendingDirectDataImport";
import { usePendingDirectDataImports } from "../hooks/usePendingDirectDataImports";
import styled from "styled-components";

const StyledConfirmModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  padding: 1.2rem 1.2rem 0;
`;

const StyledConfirmModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
`;

export const SavePendingDirectDataImportModal = ({
  isOpen,
  onClose,
  schemaExtractorOutline,
  fileNameInGcs,
  kp_content_type,
  metadata,
  onSaveSuccess,
}) => {
  const [pendingDirectDataImportName, setPendingDirectDataImportName] = useState("");
  const { mutate: createPendingDirectDataImport, isLoading } = useCreatePendingDirectDataImport();
  const { SET_ALERT_BANNER } = useBannerContext();

  // Block saving under a name that already exists in the Pending Imports
  const { data: pendingDirectDataImports } = usePendingDirectDataImports({ enabled: isOpen });
  const duplicateNameExists =
    !!pendingDirectDataImportName.trim() &&
    (pendingDirectDataImports || []).some(
      (p) =>
        p.previewName.trim().toLowerCase() ===
        pendingDirectDataImportName.trim().toLowerCase()
    );

  useEffect(() => {
    if (isOpen) setPendingDirectDataImportName("");
  }, [isOpen]);

  const handleSave = () => {
    if (!pendingDirectDataImportName.trim() || duplicateNameExists) {
      if (!pendingDirectDataImportName.trim()) {
        SET_ALERT_BANNER("Please enter a draft name", "orange", 3000);
      }
      return;
    }

    createPendingDirectDataImport(
      {
        previewName: pendingDirectDataImportName,
        schemaExtractorOutline,
        fileNameInGcs,
        kp_content_type,
        metadata,
      },
      {
        onSuccess: (data) => {
          SET_ALERT_BANNER("Draft saved successfully!", "green", 3000);
          setPendingDirectDataImportName("");
          onClose();
          onSaveSuccess?.(data?.preview);
        },
        onError: (error) => {
          // BE errors come back as { errors: [{ msg }] } via handleErrorResponse
          const msg =
            error?.response?.data?.errors?.[0]?.msg ||
            error?.response?.data?.error ||
            "Failed to save draft";
          SET_ALERT_BANNER(msg, "red", 3000);
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        backdropFilter: "blur(5px)",
        WebkitBackdropFilter: "blur(5px)",
        background: "rgba(210, 210, 210, 0.8)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "white",
          border: `1px solid ${colors2.grey10}`,
          borderRadius: "6px",
          width: "54rem",
          boxShadow: `2px 2px 10px ${colors2.grey5}`,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <StyledConfirmModalHeader>
          <UI_BODY_BOLD>Save as Draft</UI_BODY_BOLD>
          <ButtonGhost icon="X" onClick={onClose} disabled={isLoading} />
        </StyledConfirmModalHeader>
        <div style={{ padding: "1.2rem", paddingTop: 0 }}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            // oolib TextInput doesn't forward onKeyDown to the input, so we
            // catch the bubbled event here for Enter-to-save.
            onKeyDown={(e) => e.key === "Enter" && !isLoading && handleSave()}
          >
            <div>
              <TextInput
                label="Draft Name"
                value={pendingDirectDataImportName}
                onChange={(k, v) => setPendingDirectDataImportName(v)}
                placeholder="Enter draft name"
                forceFocus={isOpen}
              />
              {duplicateNameExists && (
                <UI_CAPTION color={colors2.redText}>
                  A draft with this name already exists. Please choose a
                  different name.
                </UI_CAPTION>
              )}
            </div>
            <StyledConfirmModalActions>
              <ButtonGhost onClick={onClose} disabled={isLoading}>
                Cancel
              </ButtonGhost>
              <ButtonPrimary
                onClick={handleSave}
                disabled={isLoading || duplicateNameExists}
              >
                {isLoading ? "Saving..." : "Save Draft"}
              </ButtonPrimary>
            </StyledConfirmModalActions>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavePendingDirectDataImportModal;
